import React, { useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';

/**
 * The app's one modal shell.
 *
 * Before this existed every dialog was hand-rolled, and they disagreed on almost
 * everything: three different z-indexes (z-40 / z-50 / z-[9999]), two backdrop
 * treatments, only one of ten handled Escape, two closed on a backdrop click and
 * eight did not, and two had no close button at all. Dismissal is not a per-dialog
 * design decision — a user who learns that Escape closes one dialog reasonably
 * expects it to close the next one.
 *
 * Opt out deliberately, not by omission: `dismissible={false}` for a dialog that
 * must be resolved by choosing an action.
 */

/** One place to change stacking. Toasts sit above modals; both sit above page chrome. */
export const MODAL_Z = 'z-[1000]';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Rendered in the footer, right-aligned. */
  footer?: React.ReactNode;
  /** Rendered in the footer, left-aligned — secondary info rather than actions. */
  footerLeft?: React.ReactNode;
  /** Escape and backdrop clicks close the dialog. Default true. */
  dismissible?: boolean;
  /** Show the header X. Defaults to `dismissible`. */
  showCloseButton?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Extra classes for the dialog panel. */
  className?: string;
  children?: React.ReactNode;
}

const SIZE_CLASS: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

/** Elements that can hold focus, for the focus trap. */
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), '
  + 'select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  description,
  footer,
  footerLeft,
  dismissible = true,
  showCloseButton,
  size = 'lg',
  className = '',
  children,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  // Where focus was before we opened, so it can be handed back on close.
  const restoreFocusTo = useRef<HTMLElement | null>(null);

  const requestClose = useCallback(() => {
    if (dismissible) onClose();
  }, [dismissible, onClose]);

  useEffect(() => {
    if (!open) return;

    restoreFocusTo.current = document.activeElement as HTMLElement | null;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        requestClose();
        return;
      }
      // Keep Tab inside the dialog — otherwise focus walks the page behind it.
      if (e.key === 'Tab' && panelRef.current) {
        const items = Array.from(
          panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)
        ).filter((el) => el.offsetParent !== null);
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);

    // Stop the page behind from scrolling under the dialog.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Move focus in, so keyboard and screen-reader users land in the dialog.
    const focusTimer = window.setTimeout(() => {
      const target =
        panelRef.current?.querySelector<HTMLElement>(FOCUSABLE) ?? panelRef.current;
      target?.focus();
    }, 0);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      window.clearTimeout(focusTimer);
      restoreFocusTo.current?.focus?.();
    };
  }, [open, requestClose]);

  if (!open) return null;

  const withCloseButton = showCloseButton ?? dismissible;
  const hasHeader = Boolean(title || description || withCloseButton);
  const hasFooter = Boolean(footer || footerLeft);

  return ReactDOM.createPortal(
    <div
      className={`fixed inset-0 ${MODAL_Z} flex items-center justify-center bg-black/60 backdrop-blur-sm p-4`}
      // Only a click that starts *and* ends on the backdrop dismisses. Without the
      // target check, releasing a text selection outside the panel closes the dialog
      // and discards the user's input.
      onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className={`bg-white dark:bg-stone-900 rounded-2xl shadow-xl w-full ${SIZE_CLASS[size]} max-h-[90vh] flex flex-col outline-none ${className}`}
      >
        {hasHeader && (
          <div className="flex items-start justify-between gap-4 p-5 border-b border-stone-200 dark:border-stone-700 shrink-0">
            <div className="min-w-0">
              {title && (
                <h2 className="text-lg font-semibold text-stone-900 dark:text-white flex items-center gap-2">
                  {title}
                </h2>
              )}
              {description && (
                <p className="text-sm text-stone-500 mt-0.5">{description}</p>
              )}
            </div>
            {withCloseButton && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-100 dark:hover:bg-stone-800 shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        <div className="p-5 overflow-y-auto grow">{children}</div>

        {hasFooter && (
          <div className="flex items-center justify-between gap-2 p-5 border-t border-stone-200 dark:border-stone-700 shrink-0">
            <div className="min-w-0">{footerLeft}</div>
            <div className="flex gap-2 shrink-0">{footer}</div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
